import { useCallback, useMemo, useState } from "react"
import {
  useAddApprovedUrlMutation,
  useGetPendingEmbedDomainsQuery
} from "../../redux/queries"
import { selectUserDetails } from "../../redux/slices/auth"
import { PendingEmbedDomain } from "../../types"
import { useAppSelector } from "./use-store"
import { useToast } from "./use-toast"

/** How far back to look for blocked embed requests. */
const LOOKBACK_DAYS = 30
// Well above any realistic number of blocked domains, so the Status tab can
// filter to one integration client-side without a truncated list hiding a row.
// Kept constant across callers so they all share one cached request.
const LIST_LIMIT = 100
const STORAGE_KEY_PREFIX = "bb:blocked-embed-domains-dismissed"

const storageKey = (orgId: string) => `${STORAGE_KEY_PREFIX}:${orgId}`

// localStorage throws in private-browsing / storage-disabled contexts, and a
// notification is never worth breaking a page over.
const readDismissed = (orgId: string): string[] => {
  if (!orgId) return []
  try {
    const raw = window.localStorage.getItem(storageKey(orgId))
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed)
      ? parsed.filter(h => typeof h === "string")
      : []
  } catch {
    return []
  }
}

const writeDismissed = (orgId: string, hosts: string[]) => {
  if (!orgId) return
  try {
    window.localStorage.setItem(storageKey(orgId), JSON.stringify(hosts))
  } catch {
    // ignore — the banner just reappears next session
  }
}

type UsePendingEmbedDomainsOptions = {
  enabled?: boolean
  /** Narrow to domains blocked on one integration (the Status tab view). */
  integrationId?: string
}

type UsePendingEmbedDomainsReturn = {
  /** Blocked domains as returned by the API (most recent first). */
  domains: PendingEmbedDomain[]
  /**
   * How many domains are blocked, ignoring dismissal. Org-wide unless
   * `integrationId` narrowed the list.
   */
  count: number
  /** Blocked domains the user has not dismissed the banner for yet. */
  undismissed: PendingEmbedDomain[]
  /** Dismiss the banner for every domain currently blocked. */
  dismiss: () => void
  /** Approve a domain by saving the pattern the API suggested for it. */
  approve: (domain: PendingEmbedDomain) => Promise<boolean>
  /** Host currently being approved, for a per-row spinner. */
  approvingHost: string | null
  isFetching: boolean
}

/**
 * Domains that tried to load one of the org's embeds and were blocked for not
 * being on the approved-URL list — the signal behind the Integrations nav
 * badge, the dashboard banner and the Status tab card.
 *
 * Dismissal is deliberately per-host and local: hiding the banner should not
 * hide the fact that a domain is still blocked, so `count` (the nav badge)
 * ignores dismissal while `undismissed` (the banner) respects it. A domain the
 * customer has not seen before brings the banner back.
 */
export const usePendingEmbedDomains = (
  options: UsePendingEmbedDomainsOptions = {}
): UsePendingEmbedDomainsReturn => {
  const { enabled = true, integrationId } = options

  const user = useAppSelector(selectUserDetails)
  const orgId = user?.org?.id ?? ""
  const { pushSuccessToast, pushErrorToast } = useToast()

  const { data, isFetching, refetch } = useGetPendingEmbedDomainsQuery(
    { org_id: orgId, days: LOOKBACK_DAYS, limit: LIST_LIMIT },
    { skip: !enabled || !orgId }
  )

  const [addApprovedUrl] = useAddApprovedUrlMutation()
  const [approvingHost, setApprovingHost] = useState<string | null>(null)
  // Hosts approved in this session. The API stops returning them once it
  // re-tests against the saved patterns, but dropping them locally means the
  // row disappears on click instead of after the round trip.
  const [approvedHosts, setApprovedHosts] = useState<string[]>([])

  const [dismissedHosts, setDismissedHosts] = useState<string[]>(() =>
    readDismissed(orgId)
  )

  const domains = useMemo(() => {
    const all = data?.pending_domains ?? []
    const live = all.filter(({ host }) => !approvedHosts.includes(host))
    if (!integrationId) return live
    return live.filter(({ integrations }) =>
      integrations.some(({ id }) => String(id) === String(integrationId))
    )
  }, [data, approvedHosts, integrationId])

  const undismissed = useMemo(
    () => domains.filter(({ host }) => !dismissedHosts.includes(host)),
    [domains, dismissedHosts]
  )

  const dismiss = useCallback(() => {
    const hosts = Array.from(
      new Set([...dismissedHosts, ...domains.map(({ host }) => host)])
    )
    writeDismissed(orgId, hosts)
    setDismissedHosts(hosts)
  }, [dismissedHosts, domains, orgId])

  const approve = useCallback(
    async (domain: PendingEmbedDomain) => {
      if (!orgId) {
        pushErrorToast("Unable to determine your organization for this URL.")
        return false
      }
      setApprovingHost(domain.host)
      try {
        // suggested_pattern comes from the API already in the form the matcher
        // needs (host + /*), so nobody has to hand-type the wildcard.
        await addApprovedUrl({
          org_id: orgId,
          domain_pattern: domain.suggested_pattern
        }).unwrap()
        setApprovedHosts(prev => [...prev, domain.host])
        pushSuccessToast(`${domain.host} approved`)
        // Approved URLs live in their own API slice, so approving cannot
        // invalidate this query by tag — refetch to confirm server-side.
        refetch()
        return true
      } catch {
        pushErrorToast(`Could not approve ${domain.host}. Please try again.`)
        return false
      } finally {
        setApprovingHost(null)
      }
    },
    [addApprovedUrl, orgId, pushErrorToast, pushSuccessToast, refetch]
  )

  // The server total lags an approval until the refetch lands, so discount the
  // hosts we approved that are still in the payload. Counting only those keeps
  // it exact: once the refetch drops them, nothing is double-subtracted.
  const approvedStillInPayload = useMemo(
    () =>
      (data?.pending_domains ?? []).filter(({ host }) =>
        approvedHosts.includes(host)
      ).length,
    [data, approvedHosts]
  )

  return {
    domains,
    // A narrowed list has no server-side total to report.
    count: integrationId
      ? domains.length
      : Math.max(0, (data?.total ?? 0) - approvedStillInPayload),
    undismissed,
    dismiss,
    approve,
    approvingHost,
    isFetching
  }
}
